import { Popover } from 'antd';
import Calender, { DateRangeType } from 'components/molecules/Calender';
import dayjs from 'dayjs';
import { default as CalenderIcon } from 'assets/svg/Calender';
import React, { FC, useContext, useMemo, useState } from 'react';
import { priceSimulatorDataContext } from 'contexts/PriceSimulatorContext';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, sendNotification } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PSR_DATE_RANGE_CHANGED } from 'utils/mixpanelEventNames';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { colorConstants } from 'config/colorConstants';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import { FilterContextData } from 'contexts/FilterContext';

dayjs.extend(isSameOrBefore);

type Props = {
    simulationDateRange: [any, any];
    setSimulationDateRange: Function;
    setSimulationTimePeriodSelection: Function;
    isEffectiveDateBetweenSimulationDate: Function;
    simulationTimePeriodSelection: any;
    /** When set, dates outside [anchor − weeks, anchor + weeks] are disabled in the calendar. */
    maxRangeWeeks?: number;
};
const currentDate = new Date();

function buildDisabledDates(
    range: [any, any],
    calendarMin: dayjs.Dayjs,
    maxRangeWeeks?: number,
): [string | null, string | null] {
    let min = calendarMin;
    let max: dayjs.Dayjs | null = null;

    if (maxRangeWeeks) {
        const start = range[0] ? dayjs(range[0]) : null;
        const end = range[1] ? dayjs(range[1]) : null;

        if (end?.isValid()) {
            const minFromEnd = end.subtract(maxRangeWeeks, 'week');
            if (minFromEnd.isAfter(min)) min = minFromEnd;
        }
        if (start?.isValid()) {
            max = start.add(maxRangeWeeks, 'week');
        }
    }

    return [min.format('MM-DD-YYYY'), max?.isValid() ? max.format('MM-DD-YYYY') : null];
}

export const DateRangePicker: FC<Props> = (props) => {
    const [isOpen, setIsOpen] = useState(false);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const { calenderStartDay } = useContext(FilterContextData);
    const {
        simulationDateRange,
        setSimulationDateRange,
        setSimulationTimePeriodSelection,
        simulationTimePeriodSelection,
        isEffectiveDateBetweenSimulationDate,
        maxRangeWeeks,
    } = props;

    const { viewMode } = useContext(pricingSimulatorDataContext);

    const handleDateChange = (range: DateRangeType) => {
        if (range?.[1] && dayjs(range?.[1]).isSameOrBefore(dayjs(currentDate))) {
            sendNotification('error', PricingSimulatorNotificationMessages.END_DATE_ERROR);
            return;
        }
        setSimulationDateRange([...range]);
    };

    //calender set date function
    const handleOpenChange = (newOpen: boolean, isClickOutside: boolean = true) => {
        setIsOpen(newOpen);
        if (!newOpen && !isClickOutside) {
            //only save the date when clicked on set date button
            const utcOffset = dayjs().utcOffset();
            const startDateUnix = dayjs(simulationDateRange[0]).add(utcOffset, 'm').subtract(6, 'day').unix();
            const endDateUnix = dayjs(simulationDateRange[1]).add(utcOffset, 'm').subtract(6, 'day').unix();
            setSimulationTimePeriodSelection(
                JSON.stringify({
                    start_timestamp: startDateUnix,
                    end_timestamp: endDateUnix,
                }),
            );
            isEffectiveDateBetweenSimulationDate(simulationDateRange);
            // trackEventMixpanel(PSR_DATE_RANGE_CHANGED, {
            //     startDateUnix,
            //     endDateUnix,
            // });
        } else if (!newOpen && (!simulationTimePeriodSelection || simulationDateRange.includes(null))) {
            setSimulationDateRange([null, null]); //if clicked outside dont save the change
        }
    };
    // for edit flow switch the start year to match start date
    const startYear = simulationDateRange?.[0] ? dayjs(simulationDateRange?.[0]).year() : currentDate.getFullYear();
    const dateLabel = useMemo(() => {
        let startLabel = 'Start Date';
        let endLabel = 'End Date';
        if (simulationDateRange[0])
            startLabel = dayjs(new Date(simulationDateRange[0])).format(commonConfig?.dateFormat);
        if (simulationDateRange[1]) endLabel = dayjs(new Date(simulationDateRange[1])).format(commonConfig?.dateFormat);
        return { startLabel, endLabel };
    }, [simulationDateRange]);

    const disabledDates = useMemo(
        () => buildDisabledDates(simulationDateRange, dayjs().startOf('year'), maxRangeWeeks),
        [simulationDateRange, maxRangeWeeks],
    );

    return (
        <div className="flex-grow-[2]">
            <Popover
                trigger="click"
                open={!viewMode && isOpen}
                onOpenChange={handleOpenChange}
                placement={'bottomRight'}
                content={
                    <Calender
                        startYear={startYear}
                        disabledDates={disabledDates}
                        startDay={calenderStartDay}
                        range={simulationDateRange}
                        onChange={handleDateChange}
                        onFooterClick={() => handleOpenChange(false, false)}
                        maxYear={currentDate.getFullYear() + 2}
                    />
                }
            >
                <div className="flex justify-center items-center w-full">
                    <div
                        className={` ${
                            viewMode ? ' ' : 'cursor-pointer'
                        } w-full flex justify-start gap-1 py-1 items-center px-3 bg-white border border-borderGreyAl1 hover:border-[#40a9ff]
                        ${isOpen ? 'border-[#40a9ff] shadow-md' : ''}
                        rounded-md`}
                    >
                        <div className="flex w-full justify-center">
                            <span
                                className={`${
                                    !simulationDateRange?.includes(null) && !isOpen
                                        ? 'text-subHeaderColor'
                                        : 'text-[#bfbfbf]'
                                }  font-xs`}
                            >
                                {dateLabel.startLabel}
                            </span>
                            <span
                                className={`${
                                    !simulationDateRange?.includes(null) && !isOpen
                                        ? 'text-subHeaderColor'
                                        : 'text-[#bfbfbf]'
                                } mx-1  font-xs`}
                            >
                                -
                            </span>
                            <span
                                className={`${
                                    !simulationDateRange?.includes(null) && !isOpen
                                        ? 'text-subHeaderColor'
                                        : 'text-[#bfbfbf]'
                                } font-xs`}
                            >
                                {dateLabel.endLabel}
                            </span>
                        </div>
                        <CalenderIcon stroke={colorConstants.greyAlt} />
                    </div>
                </div>
            </Popover>
        </div>
    );
};
